import { FormEvent, useState } from "react";
import { toast } from "sonner";

import { formatDate } from "@buildpy/core/lib/format";
import { QNA_BODY_MAX, QNA_TITLE_MAX, QNA_TOPIC_LABEL, QNA_TOPICS, type QnaTopic } from "@buildpy/core/modules/process-standard/qna/types";
import { PRICE_INFO_QNA_NOTICE } from "@buildpy/core/modules/process-standard/types";
import type { QnaFeedView, QnaQuestionItem } from "@buildpy/core/views/g5a/qna";
import { Button } from "@buildpy/ui/button";
import { cn } from "@buildpy/ui/cn";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@buildpy/ui/dialog";
import EmptyState from "@buildpy/ui/empty-state";
import { Input } from "@buildpy/ui/input";
import Pill from "@buildpy/ui/pill";
import { sectionCardClass } from "@buildpy/ui/section-card";
import { SimpleSelect } from "@buildpy/ui/select";
import { Textarea } from "@buildpy/ui/textarea";

import { askQuestion } from "@/api/actions/process-standard";
import Field from "@/components/field";
import FilterChips from "@/components/filter-chips";
import { Link, useRouter } from "@/lib/nav";

import { questionHref, topicHref } from "./standards-search";
import { useStandardAction } from "./use-standard-action";

export const DELETED_TEXT = "삭제된 글";

const TOPIC_OPTIONS = QNA_TOPICS.map((topic) => ({ value: topic, label: QNA_TOPIC_LABEL[topic] }));

// 질문하기 — 작성자는 「시공사 · 권역」 으로만 보인다(#390).
export const AskDialog = ({ topic, open, onOpenChange }: { topic: QnaTopic; open: boolean; onOpenChange: (open: boolean) => void }) => {
  const router = useRouter();
  const [pick, setPick] = useState<QnaTopic>(topic);
  const [title, setTitle] = useState("");
  const [body, setBody] = useState("");
  const { pending, run } = useStandardAction();

  const onSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    run(
      () => askQuestion({ topic: pick, title, body }),
      (id) => {
        onOpenChange(false);
        setTitle("");
        setBody("");
        toast.success("질문을 올렸습니다");
        router.push(questionHref(id));
      }
    );
  };

  return (
    <Dialog onOpenChange={onOpenChange} open={open}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>질문하기</DialogTitle>
          <DialogDescription>회사·이름 없이 「시공사 · 권역」 으로만 보입니다. {PRICE_INFO_QNA_NOTICE}</DialogDescription>
        </DialogHeader>
        <form className="flex flex-col gap-y-3" data-testid="ask-form" onSubmit={onSubmit}>
          <fieldset className="flex flex-col gap-y-3" disabled={pending}>
            <Field label="주제">
              <SimpleSelect onValueChange={(v) => setPick(v as QnaTopic)} options={TOPIC_OPTIONS} value={pick} />
            </Field>
            <Field label="제목">
              <Input maxLength={QNA_TITLE_MAX} name="title" onChange={(e) => setTitle(e.target.value)} value={title} />
            </Field>
            <Field label="내용">
              <Textarea className="min-h-32 text-sm" maxLength={QNA_BODY_MAX} onChange={(e) => setBody(e.target.value)} value={body} />
            </Field>
          </fieldset>
          <DialogFooter>
            <Button disabled={pending} onClick={() => onOpenChange(false)} size="sm" type="button" variant="ghost">
              취소
            </Button>
            <Button disabled={pending || title.trim() === "" || body.trim() === ""} size="sm" type="submit">
              올리기
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

// 피드 한 줄 — 지워진 질문은 제목 대신 「삭제된 글」.
export const QuestionCard = ({ question }: { question: QnaQuestionItem }) => (
  <Link className={cn(sectionCardClass({ interactive: true }), "flex flex-col gap-y-1")} href={questionHref(question.id)}>
    <div className="flex flex-wrap items-center gap-2">
      <span className={cn("text-sm font-medium", question.deleted && "text-muted-foreground")}>{question.deleted ? DELETED_TEXT : question.title}</span>
      {question.is_mine && <Pill tone="primary">내 글</Pill>}
    </div>
    <p className="text-xs text-muted-foreground tabular-nums">
      {question.author} · 답변 {question.answer_count} · {formatDate(question.created_at)}
    </p>
  </Link>
);

// Q&A 피드 — 주제별 최근 질문(#390).
const QnaFeed = ({ data }: { data: QnaFeedView }) => {
  const [asking, setAsking] = useState(false);
  const chips = QNA_TOPICS.map((topic) => ({ label: QNA_TOPIC_LABEL[topic], href: topicHref(topic) }));
  return (
    <div className="flex flex-col gap-y-4">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <FilterChips activeHref={topicHref(data.topic)} aria-label="Q&A 주제" items={chips} />
        <Button onClick={() => setAsking(true)} size="sm">
          질문하기
        </Button>
      </div>
      {data.questions.length === 0 ? (
        <EmptyState>{QNA_TOPIC_LABEL[data.topic]} 질문이 아직 없습니다.</EmptyState>
      ) : (
        <ul className="flex flex-col gap-y-2" data-testid="qna-list">
          {data.questions.map((question) => (
            <li key={question.id}>
              <QuestionCard question={question} />
            </li>
          ))}
        </ul>
      )}
      <AskDialog key={data.topic} onOpenChange={setAsking} open={asking} topic={data.topic} />
    </div>
  );
};

export default QnaFeed;
